/**
 * InClubDashboardBanner Component
 *
 * Purpose: Display club header with avatar, name, and stats
 * Similar to ClubBanner.js from the React Native app, adapted for Polaris
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Text } from '@shopify/polaris';
import { getRoomAvatarUrl, getRoomHeaderUrl, generateClubInitials } from '../utils/imageUrlHelper';

const InClubDashboardBanner = ({ inclubData }) => {
  const [headerImageLoaded, setHeaderImageLoaded] = useState(false);
  const [headerImageError, setHeaderImageError] = useState(false);
  const [avatarImageLoaded, setAvatarImageLoaded] = useState(false);
  const [avatarImageError, setAvatarImageError] = useState(false);

  // DEBUG: Log what we're receiving
  console.log('📸 InClubDashboardBanner - inclubData:', JSON.stringify(inclubData, null, 2));
  console.log('📸 defaultRoom:', inclubData?.defaultRoom);
  console.log('📸 ENV CHECK - CLOUDFRONT_DOMAIN:', process.env.REACT_APP_CLOUDFRONT_DOMAIN);
  console.log('📸 ENV CHECK - S3_BUCKET_NAME:', process.env.REACT_APP_S3_BUCKET_NAME);

  // Use default room name, falling back to inclub name
  const clubName = inclubData?.defaultRoom?.name || inclubData?.name || 'Your Club';
  const memberCount = inclubData?.memberCount || 0;
  const clubId = inclubData?._id;
  const defaultRoomId = inclubData?.defaultRoom?._id;
  const initials = generateClubInitials(clubName);

  // Get timestamps for cache busting from default room (matches main app behavior)
  const headerTimestamp = inclubData?.defaultRoom?.headerImageUpdatedAt;
  const avatarTimestamp = inclubData?.defaultRoom?.avatarImageUpdatedAt;

  // Generate URLs using room path (same as mobile app)
  const headerImageUrl = useMemo(() => {
    const url = clubId && defaultRoomId ? getRoomHeaderUrl(clubId, defaultRoomId, headerTimestamp) : null;
    console.log('📸 Header URL generated:', { clubId, defaultRoomId, headerTimestamp, url });
    return url;
  }, [clubId, defaultRoomId, headerTimestamp]);

  const avatarImageUrl = useMemo(() => {
    const url = clubId && defaultRoomId ? getRoomAvatarUrl(clubId, defaultRoomId, avatarTimestamp) : null;
    console.log('📸 Avatar URL generated:', { clubId, defaultRoomId, avatarTimestamp, url });
    return url;
  }, [clubId, defaultRoomId, avatarTimestamp]);

  // Reset states when clubId changes
  useEffect(() => {
    setHeaderImageLoaded(false);
    setHeaderImageError(false);
    setAvatarImageLoaded(false);
    setAvatarImageError(false);
  }, [clubId]);

  // Image handlers
  const handleHeaderLoad = () => {
    setHeaderImageLoaded(true);
    setHeaderImageError(false);
  };

  const handleHeaderError = () => {
    setHeaderImageLoaded(false);
    setHeaderImageError(true);
  };

  const handleAvatarLoad = () => {
    setAvatarImageLoaded(true);
    setAvatarImageError(false);
  };

  const handleAvatarError = () => {
    setAvatarImageLoaded(false);
    setAvatarImageError(true);
  };

  // Determine if we have valid images
  const hasValidHeaderImage = headerImageUrl && headerImageLoaded && !headerImageError;
  const hasValidAvatarImage = avatarImageUrl && avatarImageLoaded && !avatarImageError;

  // Styles
  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '240px',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '24px'
  };

  const backgroundStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: hasValidHeaderImage
      ? `url(${headerImageUrl})`
      : 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 50%, #f3e8ff 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)'
  };

  const contentStyle = {
    position: 'absolute',
    bottom: '24px',
    left: '24px',
    right: '24px',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '20px'
  };

  const avatarContainerStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '24px',
    overflow: 'hidden',
    border: '3px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: hasValidAvatarImage ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  };

  const avatarImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  };

  const initialsStyle = {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    lineHeight: '80px'
  };

  const detailsStyle = {
    flex: 1,
    paddingBottom: '4px'
  };

  const nameStyle = {
    fontSize: '28px',
    fontWeight: '300',
    color: 'white',
    marginBottom: '8px',
    letterSpacing: '-0.5px',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
  };

  const statsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const memberCountStyle = {
    fontSize: '14px',
    color: '#e5e7eb',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
  };

  return (
    <div style={containerStyle}>
      {/* Background Image or Gradient */}
      <div style={backgroundStyle} />

      {/* Dark Overlay for Text Readability */}
      <div style={overlayStyle} />

      {/* Content */}
      <div style={contentStyle}>
        {/* Avatar */}
        <div style={avatarContainerStyle}>
          {hasValidAvatarImage ? (
            <img src={avatarImageUrl} alt={clubName} style={avatarImageStyle} />
          ) : (
            <div style={initialsStyle}>{initials}</div>
          )}
        </div>

        {/* Club Details */}
        <div style={detailsStyle}>
          {/* Club Name */}
          <div style={nameStyle}>{clubName}</div>

          {/* Stats */}
          <div style={statsStyle}>
            {/* Member Count with Icon */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span style={memberCountStyle}>{memberCount} members</span>
          </div>
        </div>
      </div>

      {/* Hidden images for loading detection */}
      {headerImageUrl && !headerImageLoaded && (
        <img
          src={headerImageUrl}
          alt=""
          style={{ width: '1px', height: '1px', opacity: 0, position: 'absolute' }}
          onLoad={handleHeaderLoad}
          onError={handleHeaderError}
        />
      )}

      {avatarImageUrl && !avatarImageLoaded && (
        <img
          src={avatarImageUrl}
          alt=""
          style={{ width: '1px', height: '1px', opacity: 0, position: 'absolute' }}
          onLoad={handleAvatarLoad}
          onError={handleAvatarError}
        />
      )}
    </div>
  );
};

export default InClubDashboardBanner;
